import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import JsonLd from "../components/JsonLd";
import { pic } from "../lib/pic";

const POLL = "https://www.facebook.com/groups/1537243484868294";

const Kicker = ({ children, className = "" }) => (
  <p className={`font-label text-xs tracking-[0.3em] uppercase ${className}`}>{children}</p>
);

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mt-14 mb-5">{children}</h2>
);

const P = ({ children }) => (
  <p className="text-[#2E2A26]/75 text-base sm:text-lg leading-relaxed mb-6">{children}</p>
);

const PullQuote = ({ children }) => (
  <blockquote className="border-l-2 border-[#C85A38] pl-6 my-10">
    <p className="font-serif italic text-2xl sm:text-3xl text-[#2E2A26] leading-snug">{children}</p>
  </blockquote>
);

export default function StoryPage() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "I Opened a Sober Coliving in the Spanish Mountains. Everyone Told Me It Would Fail.",
    "author": { "@type": "Person", "name": "Steve Haynes", "url": "https://www.savicoliving.com/about" },
    "publisher": { "@type": "Organization", "name": "Savi Coliving", "url": "https://www.savicoliving.com" },
    "url": "https://www.savicoliving.com/story",
    "image": "https://www.savicoliving.com/images/dinner-v2.jpg",
    "about": ["sober coliving", "sober travel", "coliving Spain", "digital nomads"]
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Our Story — A Sober Coliving in the Spanish Mountains | Savi Coliving</title>
        <meta name="description" content="The first-person story of opening Savi Coliving: a sober coliving villa in the Sierra Calderona mountains near Valencia — 95-98% full since day one, and proof the sober-travel movement is real." />
        <meta property="og:title" content="I Opened a Sober Coliving in the Spanish Mountains. Everyone Told Me It Would Fail." />
        <meta property="og:description" content="Twenty years in hospitality, one unreasonable bet, and a house that's been nearly full since the day it opened." />
        <meta property="og:image" content="https://www.savicoliving.com/images/dinner-v2.jpg" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://www.savicoliving.com/story" />
      </Helmet>
      <JsonLd data={articleSchema} />

      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="pt-16" data-testid="story-hero">
        <div className="relative min-h-[62vh] sm:min-h-[72vh] flex items-end overflow-hidden">
          <img
            {...pic("quiz-bg")}
            fetchpriority="high" decoding="async"
            alt="Brick archways framing the pool and Sierra Calderona mountains at Savi Coliving"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="story-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E2A26]/30 via-[#2E2A26]/40 to-[#2E2A26]/95" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20 pt-40 text-center">
            <Kicker className="text-[#C89B7B] mb-6" style={{textShadow: '0 1px 8px rgba(0,0,0,0.4)'}}>The Savi Story</Kicker>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#FBF7F1] leading-tight mb-6" style={{textShadow: '0 2px 16px rgba(0,0,0,0.45)'}}>
              I opened a sober coliving in the Spanish mountains.<br className="hidden sm:block" />
              <span className="italic text-[#C89B7B]">Everyone told me it would fail.</span>
            </h1>
            <p className="text-[#FBF7F1]/70 text-sm">
              By Steve Haynes · Founder of Savi Coliving · Sierra Calderona, Valencia
            </p>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20" data-testid="story-article">
        <P>
          <span className="font-serif text-[#C85A38] text-6xl float-left leading-[0.8] mr-3 mt-1">T</span>
          here's a moment at almost every first dinner here. A new guest sits down at our long table — twelve or so people, paella, the sun dropping behind the Calderona — and at some point they look around and ask, quietly, like they're checking the exits:
        </P>
        <P><em className="font-serif text-xl">"So… nobody drinks? At all?"</em></P>
        <P>
          Nobody drinks. At all. And then dinner runs three hours anyway, and somewhere around hour two you can watch the realisation land on their face: <em>oh — it was never the wine doing the work.</em>
        </P>
        <img
          {...pic("dinner-v2", { sizes: "(min-width: 768px) 768px, 100vw", fallback: 768 })}
          alt="The shared dinner table in the Savi courtyard at golden hour"
          className="w-full rounded-2xl my-10 border border-stone-100"
          style={{ objectPosition: "center 60%" }}
          loading="lazy"
          data-testid="story-dinner-image"
        />

        <H2>Twenty years of watching the shortcut</H2>
        <P>
          I spent two decades in hospitality before I opened Savi. If you want to understand a person's relationship with alcohol, spend twenty years building and running the places designed around it. Every venue, every event, every "networking evening" — the entire social architecture of hospitality rests on the same load-bearing wall: drinks first, connection maybe.
        </P>
        <P>
          And it works, sort of. Alcohol is a shortcut to the <em>feeling</em> of closeness. The problem with shortcuts is that they don't take you to the actual destination. You get the warmth without the memory, the conversation without the follow-through, the thousand almost-friendships that evaporate by morning.
        </P>
        <P>
          Then remote work happened to me, the way it happened to everyone. Freedom, sunshine, a laptop — the life people post about. And quietly, underneath it, the thing nobody posts: some weeks the only person who said my name out loud was a delivery driver. There's no hashtag for eating dinner alone in paradise for the hundredth night.
        </P>
        <P>
          So I did something unreasonable. I took everything I knew about how spaces shape behaviour, found an old 18-room villa in the Sierra Calderona mountains — twenty-five minutes from Valencia, forty minutes past convenient — and removed the load-bearing wall entirely.
        </P>
        <P>No bar. No beer in the fridge. No "just one won't hurt."</P>
        <P>In Spain. The country that perfected the sobremesa.</P>

        <H2>What everyone said would happen</H2>
        <P>
          I heard every version of it. Nobody will travel to the middle of nowhere to <em>not</em> drink. You're shrinking your market to nothing. Digital nomads want Lisbon rooftops and Bali beach bars. A sober house will feel like a clinic. You'll be empty by Christmas.
        </P>
        <PullQuote>
          We opened in July 2025. We've run at 95–98% occupancy ever since — no paid advertising, no booking platforms, no discounts.
        </PullQuote>
        <P>
          As I write this, the house is fully booked until late October, there's a waitlist for autumn, and Savi has just won Best Destination Coliving at the Coliving Awards — a category built on one question: <em>will people actually travel for this?</em>
        </P>
        <P>They travel for this.</P>

        <img
          {...pic("terrace-lunch", { sizes: "(min-width: 768px) 768px, 100vw", fallback: 768 })}
          alt="Lunch laid out on the terrace at Savi Coliving, Sierra Calderona behind"
          className="w-full rounded-2xl my-10 border border-stone-100"
          loading="lazy"
        />

        <H2>Who actually comes</H2>
        <P>
          Not who you'd guess. The stereotype is someone white-knuckling recovery in the hills. The reality at our table on any given night: a founder who stopped drinking because it was costing her companies; a developer years into recovery; a consultant who never had a "problem" but got tired of every friendship being scheduled around bars; a designer who just wanted to feel sharp for once on a workation instead of writing off every morning.
        </P>
        <P>
          Some are sober. Many are what the industry now calls sober-curious. Most are simply done organising their entire social life around a drink.
        </P>
        <P>
          And the data says they're not a niche — they're a wave.
        </P>

        {/* Stats band */}
        <div className="bg-[#8C9A7C]/10 border border-[#8C9A7C]/20 rounded-2xl p-6 sm:p-8 my-10" data-testid="story-stats">
          <Kicker className="text-[#5C6A4C] mb-5">The sober-travel wave, in numbers</Kicker>
          <div className="grid grid-cols-2 gap-5">
            {[
              ["49%", "of Americans actively trying to drink less (2025)"],
              ["65%", "of Gen Z planned to cut back on alcohol this year"],
              ["+200%", "growth in \"sober travel\" searches, year on year"],
              ["4 in 5", "young travellers would consider a fully sober trip"],
            ].map(([n, label], i) => (
              <div key={i}>
                <p className="font-serif text-3xl sm:text-4xl text-[#2E2A26]">{n}</p>
                <p className="text-[#2E2A26]/55 text-xs sm:text-sm leading-snug mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <P>
          This isn't a pendulum swing; it's a generation quietly renegotiating its relationship with alcohol — and then booking travel that matches. The travel industry's answer so far has mostly been mocktail menus. With respect: a mocktail menu is not a community.
        </P>

        <H2>What a sober house is actually like</H2>
        <P>Here's the honest answer, because it surprised even me.</P>
        <P>
          It's <em>louder</em> than you'd think. Dinners run long. Conversations go deeper faster, because nobody's waiting for the second glass to say the true thing. People remember what you told them on Tuesday and ask about it on Friday — which sounds small until you realise how rare it's become.
        </P>
        <P>
          The mornings are the real product. Nobody's hungover, so the 7am hike actually happens. The cold plunge happens. The run club that someone announces at 6pm in our house app has four people at the door twenty minutes later. Deep work actually gets done, which is why founders keep extending their stays — the house is quiet from nine to one in a way no "party coliving" can promise.
        </P>
        <P>
          And the friendships hold. That's the part I'd tell my twenty-five-year-old bartending self: connection built without the shortcut doesn't evaporate. Guests leave and come back. They meet up in other countries. They message the house group chat from three time zones away because someone finally shipped the thing they'd been talking about at dinner.
        </P>
        <P>
          One judgmental podenco called Jenny supervises all of it. She has never once needed a drink to make a friend, and frankly the humans are catching up to her methodology.
        </P>
        <figure className="my-10 max-w-sm mx-auto">
          <img
            src="/images/jenny.jpg"
            alt="Jenny the podenco at Savi Coliving"
            className="w-full rounded-2xl border border-stone-100"
            loading="lazy"
            data-testid="story-jenny-image"
          />
          <figcaption className="text-center text-[#2E2A26]/40 text-xs mt-3 italic font-serif">Jenny. Head of Vetting.</figcaption>
        </figure>

        <H2>What I've learned, two years of nights at that table later</H2>
        <PullQuote>
          Sobriety was never the product. Presence is the product.
        </PullQuote>
        <P>
          Removing alcohol just removes the option to outsource connection to a substance — and it turns out that when you take the shortcut away, people don't connect less. They connect properly.
        </P>
        <P>
          That's the bet nobody believed: not that people would give something up, but that they'd travel a long way for what appears when it's gone.
        </P>
        <P>
          The sober-travel world isn't coming. It's here, it's growing, and it's not asking permission. If you're building in hospitality and you're still treating alcohol-free as a dietary requirement instead of a design principle, you're about to be surprised by your own market.
        </P>
        <P>
          And if you're one of the people this article is quietly describing — years sober, cutting back, or just tired of every trip revolving around drinks — two doors are open.
        </P>

        {/* CTAs */}
        <div className="grid sm:grid-cols-2 gap-4 my-12" data-testid="story-ctas">
          <a
            href={POLL}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-2xl p-6 text-center transition-colors"
            data-testid="story-sober-nomads-cta"
          >
            <p className="font-serif text-xl mb-1">Join Sober Nomads</p>
            <p className="text-white/75 text-sm">Our free community for sober & sober-curious travellers</p>
          </a>
          <Link
            to="/#quiz"
            className="block bg-[#C85A38] hover:bg-[#D06A4F] text-white rounded-2xl p-6 text-center transition-colors"
            data-testid="story-waitlist-cta"
          >
            <p className="font-serif text-xl mb-1">Join the waitlist</p>
            <p className="text-white/75 text-sm">The house, if you can get a room — Jenny does the final vetting</p>
          </Link>
        </div>

        <p className="font-serif italic text-2xl text-[#2E2A26] text-center mt-14">A clear mind, in good company.</p>
        <p className="text-center text-[#C85A38] font-serif text-lg mt-2">— Steve</p>
        <p className="text-center text-[#2E2A26]/40 text-xs mt-8 max-w-md mx-auto leading-relaxed">
          Steve Haynes is the founder of Savi Coliving, a sober coliving villa in the Sierra Calderona mountains near Valencia, Spain, and winner of Best Destination Coliving at the Coliving Awards 2026.
        </p>
      </article>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
